import React, { useState } from 'react';
import { IconType } from 'react-icons';
import ChakraIcon from '../components/common/ChakraIcon';

import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Flex,
  Image,
  Stack,
  Icon,
  Button,
  Divider,
  VStack,
  HStack,
  useBreakpointValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { 
  FaChild, 
  FaFemale, 
  FaUsers, 
  FaHandsHelping,
  FaHome,
  FaUniversity,
  FaUserInjured,
  FaUserFriends,
  FaArrowRight
} from 'react-icons/fa';

// Motion components
const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
const MotionText = motion(Text);
const MotionHeading = motion(Heading);

const TargetGroups: React.FC = () => {
  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.8 } }
  };
  
  const slideUp = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
  };
  
  const staggerChildren = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: "ease",
        staggerChildren: 0.2
      }
    }
  };

  // Define interface for target group data
  interface TargetGroup {
    title: string;
    description: string;
    icon: IconType;
    color: string;
    image: string;
    challenges: string[];
    approach: string;
  }

  // State for modal
  const [isOpen, setIsOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<TargetGroup | null>(null);

  const openModal = (group: TargetGroup) => {
    setSelectedGroup(group);
    setIsOpen(true);
  };

  // Target groups data
  const targetGroups: TargetGroup[] = [
    {
      title: 'Youth',
      description: 'Young people aged 15-30 who face limited educational and economic opportunities.',
      icon: FaChild,
      color: 'brand.500',
      image: '/assets/images/photo_2025-05-04_15-31-58.jpg',
      challenges: [
        'Limited access to quality education',
        'High unemployment rates',
        'Vulnerability to recruitment into armed groups',
        'Lack of safe spaces for recreation and development'
      ],
      approach: 'We provide vocational training, leadership development, peace education, and create safe spaces for youth to engage in positive activities and develop their potential.'
    },
    {
      title: 'Women',
      description: 'Women and girls who experience gender-based inequality and limited economic opportunities.',
      icon: FaFemale,
      color: 'green.500',
      image: '/assets/images/photo_2025-05-04_15-32-04.jpg',
      challenges: [
        'Gender-based violence',
        'Limited access to education and economic resources',
        'Exclusion from decision-making processes',
        'Heavy burden of unpaid care work'
      ],
      approach: 'We implement women\'s empowerment programs, provide skills training, support women\'s cooperatives, and advocate for gender equality and women\'s participation in community leadership.'
    },
    {
      title: 'Conflict-Affected Communities',
      description: 'Communities that have experienced violence and displacement due to conflict.',
      icon: FaHome,
      color: 'earth.500',
      image: '/assets/images/photo_2025-05-04_15-32-09.jpg',
      challenges: [
        'Trauma and psychosocial distress',
        'Destruction of homes and infrastructure',
        'Disrupted livelihoods',
        'Weakened social cohesion'
      ],
      approach: 'We provide trauma healing, conflict resolution training, community reconciliation processes, and support for rebuilding homes and livelihoods.'
    },
    {
      title: 'Internally Displaced Persons',
      description: 'People forced to flee their homes but who remain within the country\'s borders.',
      icon: FaUserInjured,
      color: 'brand.500',
      image: '/assets/images/photo_2025-05-04_15-32-13.jpg',
      challenges: [
        'Lack of shelter and basic necessities',
        'Limited access to services',
        'Loss of livelihoods and assets',
        'Protection concerns'
      ],
      approach: 'We provide emergency assistance, advocate for their rights and protection, and support integration into host communities or safe return to their places of origin.'
    },
    {
      title: 'School Communities',
      description: 'Students, teachers, and parents in educational settings.',
      icon: FaUniversity,
      color: 'green.500',
      image: '/assets/images/photo_2025-05-04_15-32-18.jpg',
      challenges: [
        'Inadequate school infrastructure',
        'Limited teaching and learning materials',
        'Low teacher motivation and capacity',
        'School-related violence'
      ],
      approach: 'We improve school infrastructure, provide educational materials, train teachers, and implement peace education and conflict resolution in schools.'
    },
    {
      title: 'Community Leaders',
      description: 'Traditional, religious, and elected leaders who influence community decisions.',
      icon: FaUserFriends,
      color: 'earth.500',
      image: '/assets/images/photo_2025-05-04_15-32-25.jpg',
      challenges: [
        'Limited capacity for inclusive leadership',
        'Weak governance structures',
        'Difficulty managing community conflicts',
        'Limited resources for community development'
      ],
      approach: 'We provide leadership training, conflict resolution skills, and support for inclusive and participatory decision-making processes.'
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        position="relative"
        h={{ base: '40vh', md: '50vh' }}
        bgImage="linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('/assets/images/photo_2025-05-04_15-32-30.jpg')"
        bgPosition="center"
        bgSize="cover"
        color="white"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Container maxW="container.xl" textAlign="center">
          <MotionHeading
            as={motion.h1}
            fontSize={{ base: '3xl', md: '5xl' }}
            fontWeight="bold"
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            Who We Serve
          </MotionHeading>
          
          <MotionText
            fontSize={{ base: 'lg', md: 'xl' }}
            mt={4}
            maxW="800px"
            mx="auto"
            initial="hidden"
            animate="visible"
            variants={slideUp}
          >
            Our work focuses on vulnerable and marginalized communities, including youth and those affected by conflict, poverty, and inequality.
          </MotionText>
        </Container>
      </Box>

      {/* Target Groups Section */}
      <Box py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10} alignItems="center" mb={16}>
            <MotionBox
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
            >
              <Heading
                as="h2"
                fontSize={{ base: '2xl', md: '3xl' }}
                mb={4}
              >
                Our Target Groups
              </Heading>
              
              <Divider w="100px" borderColor="brand.500" borderWidth="2px" mb={6} />
              
              <Text
                fontSize={{ base: 'md', md: 'lg' }}
                mb={6}
              >
                ALPHA's programs are designed to address the specific needs and challenges of various groups within the communities we serve.
              </Text>
              
              <Text fontSize={{ base: 'md', md: 'lg' }}>
                We work with a diverse range of beneficiaries, each facing unique challenges. Our approach is tailored to meet these specific needs while promoting dignity, empowerment, and sustainable solutions.
              </Text>
            </MotionBox>
            
            <MotionBox
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={slideUp}
            >
              <Image
                src="/assets/images/photo_2025-05-04_15-35-30.jpg"
                alt="ALPHA Target Groups"
                borderRadius="lg"
                boxShadow="lg"
                w="full"
                h="auto"
              />
            </MotionBox>
          </SimpleGrid>
          
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
            {targetGroups.map((group, index) => (
              <MotionBox
                key={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={slideUp}
                custom={index * 0.1}
                bg="white"
                borderRadius="lg"
                boxShadow="md"
                height="100%"
                overflow="hidden"
              >
                <Box position="relative" h="200px">
                  <Image
                    src={group.image}
                    alt={group.title}
                    w="100%"
                    h="100%"
                    objectFit="cover"
                  />
                  <Box
                    position="absolute"
                    top="0"
                    left="0"
                    w="100%"
                    h="100%"
                    bg="blackAlpha.600"
                    opacity="0"
                    transition="opacity 0.3s ease"
                    _groupHover={{ opacity: 1 }}
                  />
                  <Flex
                    position="absolute"
                    top="10px"
                    left="10px"
                    w="40px"
                    h="40px"
                    borderRadius="full"
                    bg={group.color}
                    color="white"
                    justify="center"
                    align="center"
                  >
                    <ChakraIcon icon={group.icon} boxSize="20px" />
                  </Flex>
                </Box>
                
                <VStack spacing={4} align="flex-start" p={6}>
                  <Heading
                    as="h3"
                    fontSize={{ base: 'xl', md: '2xl' }}
                    color={group.color}
                  >
                    {group.title}
                  </Heading>
                  
                  <Text fontSize="md">
                    {group.description}
                  </Text>
                  
                  <Button
                    variant="link"
                    color={group.color}
                    onClick={() => openModal(group)}
                  >
                    Learn More <ChakraIcon icon={FaArrowRight} boxSize="14px" ml={1} />
                  </Button>
                </VStack>
              </MotionBox>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Community-Centered Approach */}
      <Box py={{ base: 12, md: 20 }} bg="gray.50">
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10} alignItems="center">
            <MotionBox
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={slideUp}
              order={{ base: 2, md: 1 }}
            >
              <Image
                src="/assets/images/photo_2025-05-04_15-32-35.jpg"
                alt="Community-Centered Approach"
                borderRadius="lg"
                boxShadow="lg"
                w="full"
                h="auto"
              />
            </MotionBox>
            
            <MotionBox
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              order={{ base: 1, md: 2 }}
            >
              <Heading
                as="h2"
                fontSize={{ base: '2xl', md: '3xl' }}
                mb={4}
              >
                Our Community-Centered Approach
              </Heading>
              
              <Divider w="100px" borderColor="brand.500" borderWidth="2px" mb={6} />
              
              <Text fontSize={{ base: 'md', md: 'lg' }} mb={4}>
                ALPHA believes in the power of community-led development. We work alongside communities to identify their needs, build on their strengths, and develop sustainable solutions together.
              </Text>
              
              <Text fontSize={{ base: 'md', md: 'lg' }} mb={4}>
                Our approach is characterized by:
              </Text>
              
              <VStack align="flex-start" spacing={3} mb={6}>
                <HStack align="flex-start">
                  <Box w="6px" h="6px" borderRadius="full" bg="brand.500" mt={2} />
                  <Text><strong>Participation:</strong> Ensuring community members are active participants in all stages of our work.</Text>
                </HStack>
                <HStack align="flex-start">
                  <Box w="6px" h="6px" borderRadius="full" bg="brand.500" mt={2} />
                  <Text><strong>Inclusivity:</strong> Ensuring all voices, especially those of marginalized groups, are heard and valued.</Text>
                </HStack>
                <HStack align="flex-start">
                  <Box w="6px" h="6px" borderRadius="full" bg="brand.500" mt={2} />
                  <Text><strong>Capacity Building:</strong> Strengthening local skills, knowledge, and resources for sustainable impact.</Text>
                </HStack>
                <HStack align="flex-start">
                  <Box w="6px" h="6px" borderRadius="full" bg="brand.500" mt={2} />
                  <Text><strong>Contextualization:</strong> Adapting our approaches to respect local cultures and contexts.</Text>
                </HStack>
              </VStack>
            </MotionBox>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Modal for detailed view */}
      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color={selectedGroup?.color}>{selectedGroup?.title}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedGroup && (
              <VStack align="flex-start" spacing={6}>
                <Image
                  src={selectedGroup.image}
                  alt={selectedGroup.title}
                  borderRadius="md"
                  w="100%"
                  h="300px"
                  objectFit="cover"
                />
                
                <Text fontSize="lg">{selectedGroup.description}</Text>
                
                <Box w="100%">
                  <Heading as="h4" fontSize="lg" mb={3} color={selectedGroup.color}>
                    Key Challenges:
                  </Heading>
                  
                  <VStack align="flex-start" spacing={2} mb={6}>
                    {selectedGroup.challenges.map((challenge, idx) => (
                      <HStack key={idx} align="flex-start">
                        <Box w="6px" h="6px" borderRadius="full" bg={selectedGroup.color} mt={2} />
                        <Text>{challenge}</Text>
                      </HStack>
                    ))}
                  </VStack>
                </Box>
                
                <Box w="100%">
                  <Heading as="h4" fontSize="lg" mb={3} color={selectedGroup.color}>
                    Our Approach:
                  </Heading>
                  
                  <Text>{selectedGroup.approach}</Text>
                </Box>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default TargetGroups;
